<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import LiaIntegrationsAPI from 'dashboard/api/liaIntegrations';

const props = defineProps({
  version: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const store = useStore();

// Form data
const formData = ref({
  name: props.modelValue.name || '',
  company_name: props.modelValue.company_name || '',
  inbox_ids: props.modelValue.inbox_ids || [],
  erp_type: props.modelValue.erp_type || 'ixc',
  erp_url: props.modelValue.erp_url || '',
  erp_token: props.modelValue.erp_token || '',
});

// Connection test
const isTestingConnection = ref(false);
const connectionStatus = ref(null); // null, 'success', 'error'
const connectionMessage = ref('');

// Inboxes
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

// ERP Providers for LIA 3.1
const erpProviders = computed(() => [
  {
    id: 'ixc',
    name: t('LIA_AGENTS.ERP_PROVIDERS.ixc'),
    icon: 'i-lucide-server',
  },
  {
    id: 'mycore',
    name: t('LIA_AGENTS.ERP_PROVIDERS.mycore'),
    icon: 'i-lucide-database',
  },
  {
    id: 'sgp',
    name: t('LIA_AGENTS.ERP_PROVIDERS.sgp'),
    icon: 'i-lucide-hard-drive',
  },
  {
    id: 'voalle',
    name: t('LIA_AGENTS.ERP_PROVIDERS.voalle'),
    icon: 'i-lucide-cloud',
  },
  {
    id: 'hubsoft',
    name: t('LIA_AGENTS.ERP_PROVIDERS.hubsoft'),
    icon: 'i-lucide-layout-grid',
  },
]);

// Version checks
const isLia31 = computed(() => props.version === '3.1');
const isLia21 = computed(() => props.version === '2.1');

// Validation
const isValid = computed(() => {
  if (!formData.value.name?.trim()) return false;
  if (!formData.value.company_name?.trim()) return false;
  if (formData.value.inbox_ids.length === 0) return false;

  // For LIA 3.1, require ERP config
  if (isLia31.value) {
    if (!formData.value.erp_url?.trim()) return false;
    if (!formData.value.erp_token?.trim()) return false;
  }

  return true;
});

// Watch for changes and emit
watch(
  formData,
  newValue => {
    emit('update:modelValue', { ...newValue, isValid: isValid.value });
  },
  { deep: true }
);

// Toggle inbox selection
const toggleInbox = inboxId => {
  const idx = formData.value.inbox_ids.indexOf(inboxId);
  if (idx > -1) {
    formData.value.inbox_ids.splice(idx, 1);
  } else {
    formData.value.inbox_ids.push(inboxId);
  }
};

// Test ERP connection
const testConnection = async () => {
  isTestingConnection.value = true;
  connectionStatus.value = null;
  connectionMessage.value = '';

  try {
    const response = await LiaIntegrationsAPI.testConnection({
      provider: formData.value.erp_type,
      credentials: {
        api_url: formData.value.erp_url,
        api_token: formData.value.erp_token,
      },
    });

    if (response.data.success) {
      connectionStatus.value = 'success';
      connectionMessage.value =
        response.data.message || 'Conexao estabelecida!';
    } else {
      connectionStatus.value = 'error';
      connectionMessage.value = response.data.message || 'Falha na conexao';
    }
  } catch (error) {
    connectionStatus.value = 'error';
    connectionMessage.value =
      error.response?.data?.message || 'Erro ao testar conexao';
  } finally {
    isTestingConnection.value = false;
  }
};

// Get inbox icon
const getInboxIcon = inbox => {
  const channelType = inbox.channel_type || '';
  if (channelType.includes('whatsapp')) return 'i-lucide-message-circle';
  if (channelType.includes('facebook')) return 'i-lucide-facebook';
  if (channelType.includes('instagram')) return 'i-lucide-instagram';
  if (channelType.includes('email')) return 'i-lucide-mail';
  if (channelType.includes('web')) return 'i-lucide-globe';
  if (channelType.includes('telegram')) return 'i-lucide-send';
  return 'i-lucide-inbox';
};
</script>

<template>
  <div class="space-y-6 max-w-2xl mx-auto">
    <div class="text-center">
      <h2 class="text-2xl font-bold text-n-slate-12">
        {{ t('LIA_AGENTS.WIZARD.TITLE_CONFIGURE') }}
      </h2>
      <p class="mt-2 text-n-slate-11">
        <template v-if="isLia31">
          {{ t('LIA_AGENTS.WIZARD.SUBTITLE_CONFIGURE_31') }}
        </template>
        <template v-else-if="isLia21">
          {{ t('LIA_AGENTS.WIZARD.SUBTITLE_CONFIGURE_21') }}
        </template>
        <template v-else>
          {{ t('LIA_AGENTS.WIZARD.SUBTITLE_CONFIGURE_14') }}
        </template>
      </p>
    </div>

    <!-- Informacoes Basicas -->
    <div class="bg-n-solid-3 rounded-xl p-6 space-y-4">
      <h3 class="font-semibold text-n-slate-12 flex items-center gap-2">
        <span class="i-lucide-bot size-5" />
        {{ t('LIA_AGENTS.WIZARD.BASIC_INFO') }}
      </h3>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('LIA_AGENTS.WIZARD.AGENT_NAME') }} *
          </label>
          <input
            v-model="formData.name"
            type="text"
            :placeholder="t('LIA_AGENTS.WIZARD.AGENT_NAME_PLACEHOLDER')"
            class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('LIA_AGENTS.WIZARD.COMPANY_NAME') }} *
          </label>
          <input
            v-model="formData.company_name"
            type="text"
            :placeholder="t('LIA_AGENTS.WIZARD.COMPANY_NAME_PLACEHOLDER')"
            class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>
      </div>
    </div>

    <!-- Selecao de Inboxes -->
    <div class="bg-n-solid-3 rounded-xl p-6 space-y-4">
      <h3 class="font-semibold text-n-slate-12 flex items-center gap-2">
        <span class="i-lucide-inbox size-5" />
        {{ t('LIA_AGENTS.WIZARD.CHANNELS') }} *
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('LIA_AGENTS.WIZARD.CHANNELS_SUBTITLE') }}
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div
          v-for="inbox in inboxes"
          :key="inbox.id"
          class="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all"
          :class="[
            formData.inbox_ids.includes(inbox.id)
              ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
              : 'border-n-weak hover:border-n-strong',
          ]"
          @click="toggleInbox(inbox.id)"
        >
          <div
            class="w-10 h-10 rounded-lg flex items-center justify-center"
            :class="[
              formData.inbox_ids.includes(inbox.id)
                ? 'bg-woot-500 text-white'
                : 'bg-n-alpha-3 text-n-slate-11',
            ]"
          >
            <span class="size-5" :class="[getInboxIcon(inbox)]" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="font-medium text-n-slate-12 truncate">
              {{ inbox.name }}
            </p>
            <p class="text-xs text-n-slate-11 truncate">
              {{ inbox.channel_type }}
            </p>
          </div>
          <div
            v-if="formData.inbox_ids.includes(inbox.id)"
            class="w-5 h-5 rounded-full bg-woot-500 flex items-center justify-center"
          >
            <span class="i-lucide-check size-3 text-white" />
          </div>
        </div>
      </div>

      <p
        v-if="inboxes.length === 0"
        class="text-sm text-amber-600 dark:text-amber-400"
      >
        {{ t('LIA_AGENTS.WIZARD.NO_CHANNELS') }}
      </p>
    </div>

    <!-- Configuracao ERP (apenas LIA 3.1) -->
    <div v-if="isLia31" class="bg-n-solid-3 rounded-xl p-6 space-y-4">
      <h3 class="font-semibold text-n-slate-12 flex items-center gap-2">
        <span class="i-lucide-database size-5" />
        {{ t('LIA_AGENTS.WIZARD.ERP_INTEGRATION') }} *
      </h3>

      <!-- Selecao do ERP -->
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ t('LIA_AGENTS.WIZARD.ERP_SYSTEM') }}
        </label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="erp in erpProviders"
            :key="erp.id"
            type="button"
            class="flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all"
            :class="[
              formData.erp_type === erp.id
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'border-n-weak hover:border-n-strong text-n-slate-11',
            ]"
            @click="formData.erp_type = erp.id"
          >
            <span class="size-4" :class="[erp.icon]" />
            {{ erp.name }}
          </button>
        </div>
      </div>

      <!-- Credenciais -->
      <div class="grid grid-cols-1 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('LIA_AGENTS.WIZARD.API_URL') }} *
          </label>
          <input
            v-model="formData.erp_url"
            type="url"
            :placeholder="t('LIA_AGENTS.WIZARD.API_URL_PLACEHOLDER')"
            class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('LIA_AGENTS.WIZARD.API_TOKEN') }} *
          </label>
          <input
            v-model="formData.erp_token"
            type="password"
            :placeholder="t('LIA_AGENTS.WIZARD.API_TOKEN_PLACEHOLDER')"
            class="w-full px-3 py-2 bg-n-alpha-3 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>
      </div>

      <!-- Botao Testar Conexao -->
      <div class="flex items-center gap-4">
        <button
          type="button"
          class="flex items-center gap-2 px-4 py-2 bg-n-alpha-3 hover:bg-n-alpha-4 rounded-lg text-n-slate-12 transition-colors disabled:opacity-50"
          :disabled="
            !formData.erp_url || !formData.erp_token || isTestingConnection
          "
          @click="testConnection"
        >
          <span
            v-if="isTestingConnection"
            class="i-lucide-loader-2 size-4 animate-spin"
          />
          <span v-else class="i-lucide-plug size-4" />
          {{ t('LIA_AGENTS.WIZARD.TEST_CONNECTION') }}
        </button>

        <div v-if="connectionStatus" class="flex items-center gap-2">
          <span
            v-if="connectionStatus === 'success'"
            class="i-lucide-check-circle size-5 text-green-500"
          />
          <span v-else class="i-lucide-x-circle size-5 text-red-500" />
          <span
            class="text-sm"
            :class="[
              connectionStatus === 'success'
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400',
            ]"
          >
            {{ connectionMessage }}
          </span>
        </div>
      </div>
    </div>

    <!-- Configuracao Google Calendar (apenas LIA 2.1) -->
    <div v-if="isLia21" class="bg-n-solid-3 rounded-xl p-6 space-y-4">
      <h3 class="font-semibold text-n-slate-12 flex items-center gap-2">
        <span class="i-lucide-calendar size-5" />
        {{ t('LIA_AGENTS.WIZARD.ERP_INTEGRATION') }}
      </h3>

      <div class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p class="text-sm text-blue-700 dark:text-blue-300">
          <span class="i-lucide-info size-4 inline mr-1" />
          {{ t('LIA_AGENTS.WIZARD.CALENDAR_INFO') }}
        </p>
      </div>
    </div>

    <!-- Validacao -->
    <div
      v-if="!isValid"
      class="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg"
    >
      <p
        class="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2"
      >
        <span class="i-lucide-alert-triangle size-4" />
        {{ t('LIA_AGENTS.WIZARD.VALIDATION_ERROR') }}
      </p>
    </div>
  </div>
</template>
